/**
 * Whether someone has seen the Settings intro, kept per person.
 *
 * Settings has no per-user preference store of its own (the profile settings
 * it syncs are the employee app's dietary, vehicle and workplace sections,
 * and the appearance fields), so the flag lives in localStorage, keyed by the
 * viewer's id. A shared machine therefore shows it once to each person who
 * signs in, and a person on a new browser sees it once more. Moving it onto
 * the profile later is this file alone.
 *
 * Private mode and quota errors read as "not seen" and are otherwise
 * swallowed: the worst case is the intro once more, never a broken page.
 */

/** Bump to show a rewritten intro to everyone again. */
export const INTRO_VERSION = 1;

/** How they left it: finished on the last page, or skipped part way. */
export type IntroSeenHow = 'finished' | 'skipped';

export interface IntroSeen {
  readonly at: string;
  readonly how: IntroSeenHow;
  readonly version: number;
}

export function introSeenKey(userId: string): string {
  return `settings.intro.v${INTRO_VERSION}.seen:${userId}`;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** What was recorded for this person, or null when they have not seen this version. */
export function readIntroSeen(userId: string | null | undefined): IntroSeen | null {
  if (!userId) return null;
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(introSeenKey(userId)) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<IntroSeen> | null;
    if (!parsed || typeof parsed !== 'object' || parsed.version !== INTRO_VERSION) return null;
    return { at: String(parsed.at ?? ''), how: parsed.how === 'finished' ? 'finished' : 'skipped', version: INTRO_VERSION };
  } catch {
    // Unreadable: treat it as seen rather than show the intro on every visit.
    return { at: '', how: 'skipped', version: INTRO_VERSION };
  }
}

/** Record that this person has seen it. False when it could not be kept (private mode). */
export function writeIntroSeen(userId: string | null | undefined, how: IntroSeenHow, now = new Date()): boolean {
  if (!userId) return false;
  const value: IntroSeen = { at: now.toISOString(), how, version: INTRO_VERSION };
  try {
    const store = storage();
    if (!store) return false;
    store.setItem(introSeenKey(userId), JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
